import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';

import { AngularFirestore, AngularFirestoreCollection } from '@angular/fire/compat/firestore';
import { Observable, Subscription } from 'rxjs';

import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { SelectionModel } from '@angular/cdk/collections';
import { ToastrService } from 'ngx-toastr';

import { FireUser, SelectExhibits } from '../firestore-models/user';
import { Exhibit } from '../firestore-models/exhibit';
import { ExhibitAttribute } from '../firestore-models/exhibit-attribute';
import { ProjectLanguage } from '../firestore-models/project-language';
import { ApiUser, ApiExhibit } from '../api-client-module';
import { ApiSetExhibitsRequest } from '../api-client-module'
import { ApiSetUsersRequest } from '../api-client-module';
import { ApiClientService } from '../api-client.service';
import { ConfigurationService } from '../configuration.service';
import { SpinnerService } from '../services/spinner.service';
import { AppConst } from '../app-const';
import { SessionService } from '../session.service';

import { ExhibitDialogComponent } from './exhibit-dialog/exhibit-dialog.component';
import { MessageDialogComponent } from '../common/message-dialog/message-dialog.component';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-exhibit',
  templateUrl: './exhibit.component.html',
  styleUrls: ['./exhibit.component.css'],
  standalone: false,
})
export class ExhibitComponent implements OnInit, OnDestroy {

  // 展示物コレクション
  private exhibitsCollection!: AngularFirestoreCollection<Exhibit>;
  exhibitsObservable!: Observable<Exhibit[]>;
  exhibitsSubscribe!: Subscription;
  // 展示属性コレクション
  private exhibitAttributesCollection!: AngularFirestoreCollection<ExhibitAttribute>;
  exhibitAttributesObservable!: Observable<ExhibitAttribute[]>;
  exhibitAttributesSubscribe!: Subscription;

  //各展示情報に紐付くプロジェクトID
  projectId: string;
  //各展示情報に紐付くプロジェクトの言語情報
  projectLanguages!: Array<ProjectLanguage>;

  //展示情報テーブル
  displayedColumns: string[] = ['position', 'edit', 'select', 'exhibit_id', 'attribute', 'name', 'file', 'date'];
  dataSource = new MatTableDataSource<Exhibit>();
  selection = new SelectionModel<Exhibit>(true, []);

  protected ATTR_SEARCH_WORD_PREFIX: string = 'exhibitAttributes';
  exhibitAttributes!: Array<ExhibitAttribute>;

  searchFilter: any = {};

  attrCollectionName = AppConst.FIRESTORE_EXHIBIT_ATTR;
  uploadedFilesCollectionName = AppConst.FIRESTORE_UPLOADED_FILES;

  // ユーザータイプ
  readonly USER_TYPE_USER: number = AppConst.MANAGE_USER_TYPE_USER;
  user?: ApiUser;
  loginUser: FireUser | null = null;
  loginUserType!: number;
  exhibitIds: Array<SelectExhibits> | null; 
  isEdit: boolean = false;
  updateExhibits: Array<SelectExhibits> = [];
  addExhibit: Array<string> = [];

  projectBaseLang: string = 'ja';

  @ViewChild(MatPaginator, {static: true}) paginator!: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort!: MatSort;

  constructor(
    public dialog: MatDialog,
    private sessionService: SessionService,
    private angularFirestore: AngularFirestore,
    private apiClientService: ApiClientService,
    private spinnerService: SpinnerService,
    private toastr: ToastrService,
    private configurationService: ConfigurationService,
    private translate: TranslateService
  ) {
    this.projectId = this.configurationService.getProjectId();
    this.exhibitIds = this.configurationService.getExhibitIds();
    this.isEdit = this.configurationService.getIsEdit();
    if (this.sessionService.session.isLogin) {
      this.loginUser = this.sessionService.session.user;
      this.loginUserType = this.sessionService.session.user!.user_type;
    }
    this.user = {
      user_id: '',
      user_name: '',
      user_desc: '',
      user_type: 1,
      is_edit: false,
      project_ids: [],
      exhibit_ids: []
    };
 }

  ngOnInit(): void {
    this.projectLanguages = this.configurationService.getProject().languages!;
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    this.setCustomFilter();
    this.projectBaseLang = this.configurationService.getProject().project_base_lang;
    // 展示物取得
    this.exhibitsCollection = this.angularFirestore.collection<Exhibit>(
      AppConst.FIRESTORE_EXHIBITS, ref => ref.where('project_id', '==', this.projectId).orderBy('exhibit_id', 'asc'));
    this.exhibitsObservable = this.exhibitsCollection.valueChanges({ idField: 'id' });
    this.exhibitsSubscribe = this.exhibitsObservable.subscribe(exhibits => {
      if (this.loginUser && this.loginUserType === AppConst.MANAGE_USER_TYPE_USER) {
        if (this.addExhibit && this.addExhibit.length > 0) {
          for (const exhibit of exhibits) {
            // 追加した展示物を見つけて、ユーザーの展示物リストを更新する
            if (exhibit.project_id === this.addExhibit[0] && exhibit.exhibit_id === this.addExhibit[1]) {
              if (this.exhibitIds) {
                this.updateExhibits = this.exhibitIds;
              }
              // ユーザーの展示物リストがない場合
              if (this.updateExhibits && this.updateExhibits.length < 1) {
                if (exhibit.id) {
                  this.updateExhibits.push({project_id:this.addExhibit[0], exhibit_ids:[exhibit.id]});
                }
              } else {
                let isUpdated = false;
                // ユーザーの展示物リストがあり該当のプロジェクトIDが存在する場合
                for (const updateExhibit of this.updateExhibits) {
                  if (updateExhibit.project_id === this.addExhibit[0]) {
                    if (exhibit.id) {
                      if (!updateExhibit.exhibit_ids.includes(exhibit.id)) {
                        updateExhibit.exhibit_ids.push(exhibit.id);
                        isUpdated = true;
                      }
                    }
                  }
                }
                // ユーザーの展示物リストがあり該当のプロジェクトIDが存在しない場合
                if (isUpdated === false) {
                  if (exhibit.id) {
                    this.updateExhibits.push({project_id:this.addExhibit[0], exhibit_ids:[exhibit.id]});
                  }
                }
              }
              if (this.updateExhibits && this.updateExhibits.length > 0) {
                this.configurationService.setExhibitIds(this.updateExhibits);
                this.updateUser();
                this.updateExhibits = [];
              }
              this.addExhibit = [];
            }
          }
        }
      }
      this.exhibitIds = this.configurationService.getExhibitIds();
      let targetExhibits = [];
      if (this.loginUserType === AppConst.MANAGE_USER_TYPE_USER) {
        if (this.exhibitIds) {
          for (const exhibitIds of this.exhibitIds) {
            if (exhibitIds.project_id === this.projectId) {
              for (const exhibit_id of exhibitIds.exhibit_ids) {
                for (const exhibit of exhibits) {
                  if (exhibit_id === exhibit.id) {
                    targetExhibits.push(exhibit);
                  }
                }
              } 
            }
          }
        }
      } else {
        targetExhibits = exhibits;
      }
      this.dataSource.data = this.addFilterValue(targetExhibits);
    });
    // 展示属性取得
    this.exhibitAttributesCollection = this.angularFirestore.collection<ExhibitAttribute>(
      AppConst.FIRESTORE_EXHIBIT_ATTR, ref => ref.where('project_id', '==', this.projectId).orderBy('attribute_id', 'asc'));
    this.exhibitAttributesObservable = this.exhibitAttributesCollection.valueChanges({ idField: 'id' });
    this.exhibitAttributesSubscribe = this.exhibitAttributesObservable.subscribe(exhibitAttributes => {
      this.exhibitAttributes = exhibitAttributes;
    });
  }

  ngOnDestroy(): void {
    this.exhibitsSubscribe.unsubscribe();
    this.exhibitAttributesSubscribe.unsubscribe();
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  masterToggle() {
    this.isAllSelected() ?
      this.selection.clear() :
      this.dataSource.data.forEach(row => this.selection.select(row));
  }

  checkboxLabel(row?: Exhibit): string {
    if (!row) {
      return `${this.isAllSelected() ? 'select' : 'deselect'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'}`;
  }

  //編集ダイアログ
  openEditDialog(exhibit: Exhibit): void {
    let apiExhibit: ApiExhibit = {
      id: exhibit.id,
      exhibit_id: exhibit.exhibit_id,
      exhibit_attribute_id: exhibit.exhibit_attribute_id,
      exhibit_names: exhibit.exhibit_names,
      exhibit_descs: exhibit.exhibit_descs,
      exhibit_files: exhibit.exhibit_files,
      latitude: exhibit.exhibit_geopoint ? exhibit.exhibit_geopoint.latitude : AppConst.LATITUDE_INITIAL_VALUE,
      longitude: exhibit.exhibit_geopoint ? exhibit.exhibit_geopoint.longitude : AppConst.LONGITUDE_INITIAL_VALUE,
      show_marker: exhibit.show_marker,
      youtube_codes: exhibit.youtube_codes,
      use_youtube: exhibit.use_youtube,
      externallink_names: exhibit.externallink_names,
      externallink_urls: exhibit.externallink_urls,
      use_externallink: exhibit.use_externallink,
      not_overlap_guide: exhibit.not_overlap_guide,
      exhibit_detail_ids: exhibit.exhibit_detail_ids,
      reservation_facility_ids: exhibit.reservation_facility_ids,
      project_id: exhibit.project_id,
      enabled: exhibit.enabled,
    };
    const dialogRef = this.dialog.open(ExhibitDialogComponent, {
      width: '85%',
      disableClose: true,
      data: { change_type: AppConst.DATA_CHANGE_TYPE_UPDATE, exhibit: apiExhibit }
    });
  }

  updateUser(): void {
    // 一般ユーザーが展示物を追加した場合は、ユーザーの展示物リストを更新
    if (this.loginUserType === AppConst.MANAGE_USER_TYPE_USER) {
      if (this.loginUser) {
        this.user!.user_id = this.loginUser?.user_id;
        this.user!.user_name = this.loginUser?.user_name;
        this.user!.user_desc = this.loginUser?.user_desc;
        this.user!.user_type = this.loginUser?.user_type;
        this.user!.is_edit = this.loginUser?.is_edit;
        this.user!.project_ids = this.loginUser?.project_ids;
        this.user!.exhibit_ids = this.updateExhibits;
        let request: ApiSetUsersRequest = {
          access_token: '',
          change_type: AppConst.DATA_CHANGE_TYPE_UPDATE,
          users: [this.user!]
        }
        this.apiClientService.setUsers(request).then(
          response => {
          }
        ).catch(
          error => {
            console.log(error);
          }
        ).finally(
          () => {
          }
        );
      }
    }
  }

  //追加ダイアログ
  openAddDialog(): void {
    const dialogRef = this.dialog.open(ExhibitDialogComponent, {
      width: '85%',
      disableClose: true,
      data: { change_type: AppConst.DATA_CHANGE_TYPE_ADD, exhibit: null }
    });
  }

  //削除
  deleteData(): void {
    const dialogRef = this.dialog.open(MessageDialogComponent, {
      width: '420px',
      height: '180px',
      disableClose: true,
      data: { message: this.translate.instant('選択されたデータを削除します。よろしいですか？') }
    });

    dialogRef.afterClosed().subscribe(result => {
      let selectedExhibits: Array<ApiExhibit> = new Array<ApiExhibit>();
      for (const row of this.dataSource.data) {
        if (this.selection.isSelected(row)) {
          const selectedExhibit: ApiExhibit = {
            id: row.id,
            exhibit_id: row.exhibit_id,
            exhibit_attribute_id: row.exhibit_attribute_id,
            exhibit_names: row.exhibit_names,
            exhibit_descs: row.exhibit_descs,
            exhibit_files: row.exhibit_files,
            latitude: row.exhibit_geopoint.latitude,
            longitude: row.exhibit_geopoint.longitude,
            show_marker: row.show_marker,
            youtube_codes: row.youtube_codes,
            use_youtube: row.use_youtube,
            externallink_names: row.externallink_names,
            externallink_urls: row.externallink_urls,
            use_externallink: row.use_externallink,
            not_overlap_guide: row.not_overlap_guide,
            exhibit_detail_ids: row.exhibit_detail_ids,
            reservation_facility_ids: row.reservation_facility_ids,
            project_id: row.project_id,
            enabled: row.enabled,
          };
          selectedExhibits.push(selectedExhibit);
        }
      }
      if (!!result) {
        let request: ApiSetExhibitsRequest = {
          access_token: '',
          change_type: AppConst.DATA_CHANGE_TYPE_DELETE,
          exhibits: selectedExhibits
        }
        this.spinnerService.openSpinner();
        this.apiClientService.setExhibits(request).then(
          response => {
            if (response!.ret === AppConst.API_RET_SUCCESS) {
              this.toastr.success(this.translate.instant('削除しました。'));
            } else {
              if (response!.ret === AppConst.API_RET_IN_USE_DATA) {
                this.toastr.error(this.translate.instant('削除できませんでした。フロア・表示グループ情報で利用中のデータが含まれている可能性があります。'));
              } else {
                this.toastr.error(this.translate.instant('削除できませんでした。'));
              }
            }
          }
        ).catch(
          error => {
            console.log(error);
            this.toastr.error(this.translate.instant('削除できませんでした。'));
          }
        ).finally(
          () => {
            this.spinnerService.closeSpinner();
            this.selection.clear();
          }
        );
      }
    });
  }

  sortData(sort: Sort) {
    const data = this.dataSource.data.slice();
    if (!sort.active || sort.direction === '') {
      this.dataSource.data = data;
      return;
    }
    const compare = (a: number | string, b: number | string, isAsc: boolean) => {
      return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
    }

    this.dataSource.data = data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'exhibit_id':
          return compare(a.exhibit_id, b.exhibit_id, isAsc);
        case 'attribute':
          const aAttr = a.exhibit_attribute_id ? a.exhibit_attribute_id : '';
          const bAttr = b.exhibit_attribute_id ? b.exhibit_attribute_id : '';
          return compare(aAttr, bAttr, isAsc);
        case 'name':
          const aName = a.exhibit_names.filter((d) => { return d.lang_code == this.projectLanguages[0].lang_code })[0].name;
          const bName = b.exhibit_names.filter((d) => { return d.lang_code == this.projectLanguages[0].lang_code })[0].name;
          return compare(aName, bName, isAsc);
        case 'date':
          return compare(a.updated.toMillis(), b.updated.toMillis(), isAsc);
        default:
          return 0;
      }
    });
  }

  setCustomFilter() {
    // NOTE: AND検索
    this.dataSource.filterPredicate = (data: any, filter: any) => {
      let matchCount = 0;
      let keys = Object.keys(filter);
      for (const key of keys) {
        if (!filter[key]) {
          // フィルタに値がない場合は全抽出
          matchCount++;
        } else {
          if (data[key].trim().toLowerCase().indexOf(filter[key]) !== -1) {
            matchCount++;
          }
        }
      }
      return (keys.length === matchCount);
    };
  }

  addFilterValue(docs: Array<any>): Array<any> {
    for (const doc of docs) {
      let filterValue = '';
      for (const value of doc.exhibit_names) {
        filterValue += value.name + ' '
      }
      doc['filterValue'] = filterValue;
      doc['filterValueAttr'] = this.ATTR_SEARCH_WORD_PREFIX + doc.exhibit_attribute_id;
    }
    return docs;
  }

  applyFilter(event: Event) {
    this.selection.clear();
    const filterValue = (event.target as HTMLInputElement).value;
    this.searchFilter['filterValue'] = filterValue.trim().toLowerCase();
    this.dataSource.filter = this.searchFilter;
  }

  applyFilterAttr(event: any) {
    this.selection.clear();
    let filterValue = '';
    if (event.value) {
      filterValue = this.ATTR_SEARCH_WORD_PREFIX + event.value;
    }
    this.searchFilter['filterValueAttr'] = filterValue.trim().toLowerCase();
    this.dataSource.filter = this.searchFilter;
  }

}
